/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToCartBehavior } from '@msdyn365-commerce/components';
import { getCartState } from '@msdyn365-commerce/global-state';
import { getCatalogId, getUrlSync } from '@msdyn365-commerce/core';
import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';
import {
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxData,
    IBuyboxProductQuantityViewProps,
    IBuyboxResources as IBuyboxExtentionResources,
    IBuyboxState,
    IBuyboxViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps
} from '@msdyn365-commerce-modules/buybox';
import { NotificationsManager, SuccessNotification } from '@msdyn365-commerce-modules/notifications-core';
import // ArrayExtensions,
    '@msdyn365-commerce-modules/retail-actions';
import { IncrementalQuantity, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { reaction } from 'mobx';
import * as React from 'react';

import { IBuyboxProps as IBuyboxExtentionProps, IBuyboxProps } from '../definition-extensions/buybox.ext.props.autogenerated';
import { getProductClassificationDisplayValue, isPvoProductClassification } from '../utils/product-classification';
import { renderAddToOrderTemplateButton } from './components/buybox-add-to-order-template';
import { IBuyboxData as IBuyboxViewData } from './buybox.data';
import { getUomConversionMap, convertAvailableQuantityToUOM, getBaseUomSymbol } from '../utils/uom-conversions'
/**
 * BuyBoxConstants enum.
 */
export enum BuyBoxConstants {
    zero = 0
}

interface IDisplayAttribute {
    key: string;
    name: string;
    value: string;
    recordId?: number;
}

const getPrimitiveTextValue = (value: unknown): string => {
    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return value.toString();
    }

    return '';
};

const normalizeAttributeName = (name?: unknown): string =>
    getPrimitiveTextValue(name)
        .replace(/[^a-z0-9]/gi, '')
        .toLocaleLowerCase();

const getAttributeValue = (attribute: any): string => {
    const textValue = getPrimitiveTextValue(attribute?.TextValue);
    if (textValue) {
        return textValue;
    }

    if (attribute?.BooleanValue !== undefined && attribute.BooleanValue !== null) {
        return attribute.BooleanValue ? 'Yes' : 'No';
    }

    if (attribute?.IntegerValue !== undefined && attribute.IntegerValue !== null) {
        return attribute.IntegerValue.toString();
    }

    if (attribute?.FloatValue !== undefined && attribute.FloatValue !== null) {
        return attribute.FloatValue.toString();
    }

    if (attribute?.CurrencyValue !== undefined && attribute.CurrencyValue !== null) {
        return attribute.CurrencyValue.toString();
    }

    const extensionValue = attribute?.ExtensionProperties?.find((property: any) => !!property?.Value)?.Value;
    const extensionStringValue = getPrimitiveTextValue(extensionValue?.StringValue);
    if (extensionStringValue) {
        return extensionStringValue;
    }

    if (extensionValue?.BooleanValue !== undefined && extensionValue.BooleanValue !== null) {
        return extensionValue.BooleanValue ? 'Yes' : 'No';
    }

    if (extensionValue?.DecimalValue !== undefined && extensionValue.DecimalValue !== null) {
        return extensionValue.DecimalValue.toString();
    }

    if (extensionValue?.IntegerValue !== undefined && extensionValue.IntegerValue !== null) {
        return extensionValue.IntegerValue.toString();
    }

    if (extensionValue?.LongValue !== undefined && extensionValue.LongValue !== null) {
        return extensionValue.LongValue.toString();
    }

    return '';
};

/** Commerce product attribute `Name` from HQ (e.g. TextValue on PDP). */
const CASE_DESCRIPTION_COMMERCE_NAME = 'Case Description';
const CASE_DESCRIPTION_NORMALIZED_KEY = 'casedescription';

const isCaseDescriptionAttribute = (attribute: any): boolean => {
    const nameRaw = getPrimitiveTextValue(attribute?.Name).trim();
    if (nameRaw && nameRaw.toLocaleLowerCase() === CASE_DESCRIPTION_COMMERCE_NAME.toLocaleLowerCase()) {
        return true;
    }

    const normalizedFromName = normalizeAttributeName(attribute?.Name);
    const normalizedFromKey = normalizeAttributeName(attribute?.KeyName);
    return normalizedFromName === CASE_DESCRIPTION_NORMALIZED_KEY || normalizedFromKey === CASE_DESCRIPTION_NORMALIZED_KEY;
};

interface ICaseDescriptionRow {
    caseNumber: string;
    bacteriaName: string;
}

/**
 * Header prefix variants in Case Description TextValue, e.g.
 * "Case # Bacteria Name ...", "Case #  ...", or "Case# ...".
 */
const CASE_DESCRIPTION_HEADER_PREFIX_PATTERN = /^Case\s*#(?:\s+Bacteria\s+Name)?\s*/i;

const parseHyphenSeparatedCaseRows = (dataSection: string): ICaseDescriptionRow[] => {
    const rows: ICaseDescriptionRow[] = [];
    const rowPattern = /(\d+-\S+)(?:\s+(.+?))?(?=\s+\d+-\S+|$)/gs;
    let match: RegExpExecArray | null = rowPattern.exec(dataSection);
    while (match) {
        rows.push({
            caseNumber: match[1],
            bacteriaName: (match[2] ?? '').trim()
        });
        match = rowPattern.exec(dataSection);
    }

    return rows;
};

const parseSpaceSeparatedCaseRows = (dataSection: string): ICaseDescriptionRow[] => {
    const rows: ICaseDescriptionRow[] = [];
    const rowPattern = /(\d+)\s+(.+?)(?=\s+\d+\s|$)/gs;
    let match: RegExpExecArray | null = rowPattern.exec(dataSection);
    while (match) {
        rows.push({
            caseNumber: match[1],
            bacteriaName: match[2].trim()
        });
        match = rowPattern.exec(dataSection);
    }

    return rows;
};

const parseCaseDescriptionTableData = (sourceText: string): ICaseDescriptionRow[] => {
    const trimmed = sourceText.trim();
    if (!trimmed) {
        return [];
    }

    const lineRows = trimmed
        .split('\n')
        .map(line => line.trim())
        .filter(line => /^\d+/.test(line))
        .map(line => {
            const hyphenMatch = line.match(/^(\d+-\S+)\s*(.*)$/);
            if (hyphenMatch) {
                return { caseNumber: hyphenMatch[1], bacteriaName: hyphenMatch[2].trim() };
            }

            const [caseNumber, ...bacteriaParts] = line.split(/\s+|\t+/);
            return { caseNumber, bacteriaName: bacteriaParts.join(' ').trim() };
        })
        .filter(row => row.caseNumber);

    if (lineRows.length) {
        return lineRows;
    }

    const dataSection = trimmed.replace(CASE_DESCRIPTION_HEADER_PREFIX_PATTERN, '').trim();
    if (!dataSection) {
        return [];
    }

    if (/\d+-/.test(dataSection)) {
        const hyphenRows = parseHyphenSeparatedCaseRows(dataSection);
        if (hyphenRows.length) {
            return hyphenRows;
        }
    }

    return parseSpaceSeparatedCaseRows(dataSection);
};

const getCaseDescriptionAttributeValue = (attributes: any[] | undefined): string => {
    if (!attributes?.length) {
        return '';
    }

    const list = attributes.slice?.() ?? attributes;
    for (const attribute of list) {
        if (isCaseDescriptionAttribute(attribute)) {
            return getAttributeValue(attribute);
        }
    }

    return '';
};

const getPdpDisplayAttributes = (attributes: any[] | undefined, isPvoItem: boolean): IDisplayAttribute[] => {
    if (!attributes?.length) {
        return [];
    }

    const attributeDisplayOrder = ['itemid', 'packagesize', 'storagetemperature', 'hazmat', 'qtyperpallet'];
    const sharedVisibleAttributes = new Set(['itemid', 'packagesize', 'storagetemperature', 'hazmat']);
    const nonPvoOnlyVisibleAttributes = new Set(['qtyperpallet']);

    return attributes
        .map((attribute: any): IDisplayAttribute | null => {
            const name = getPrimitiveTextValue(attribute?.Name) || getPrimitiveTextValue(attribute?.KeyName);
            const keyName = attribute?.KeyName ?? '';
            const value = getAttributeValue(attribute);
            const normalizedNames = [normalizeAttributeName(name), normalizeAttributeName(keyName)];

            if (!name) {
                return null;
            }

            const matchedKey = attributeDisplayOrder.find(key => normalizedNames.includes(key));
            if (!matchedKey) {
                return null;
            }

            if (!sharedVisibleAttributes.has(matchedKey) && (isPvoItem || !nonPvoOnlyVisibleAttributes.has(matchedKey))) {
                return null;
            }

            return {
                key: matchedKey,
                name,
                value,
                recordId: attribute?.RecordId
            };
        })
        .filter((attribute): attribute is IDisplayAttribute => attribute !== null)
        .sort((firstAttribute, secondAttribute) => {
            return attributeDisplayOrder.indexOf(firstAttribute.key) - attributeDisplayOrder.indexOf(secondAttribute.key);
        });
};

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

/**
 * RenderQuantity.
 * @param quantityComponent - QuantityComponent.
 * @param callbacks - Callbacks.
 * @param props - Props.
 * @param state - State.
 * @param extentionResources - ExtentionResources.
 * @param quantityLimitsMessages - QuantityLimitsMessages.
 * @param max - Max.
 * @param telemetryContent - TelemetryContent.
 * @param unitOfMeasure - Unit Of Measure.
 * @returns -- Returns.
 */
const renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    min: number | undefined,
    max: number | undefined,
    applyDefaultOrderSettings?: boolean,
    telemetryContent?: ITelemetryContent,
    unitOfMeasure?: React.ReactNode
): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                min={min}
                max={max}
                applyDefaultOrderSettings={applyDefaultOrderSettings}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            />
            {unitOfMeasure}
            {quantityLimitsMessages}
        </Node>
    );
};

/**
 * RenderAddToCart.
 * @param addToCart - AddToCart.
 * @returns -- Returns.
 */
const renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

/**
 * RenderAddToOrderTemplate.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToOrderTemplate = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToOrderTemplate || !product) {
        return null;
    }

    const orderTemplateButton = renderAddToOrderTemplateButton(props, state, callbacks, product);

    return (
        <Node {...props.addToOrderTemplate.ContainerProps}>
            {props.addToOrderTemplate.errorBlock}
            {orderTemplateButton}
        </Node>
    );
};

/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};

/**
 * BuyboxView.
 * @param props - Props.
 * @returns -- Returns.
 */
const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData & IBuyboxViewData>> = props => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const {
        ModuleProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        MediaGalleryContainerProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        description,
        quantity,
        title,
        inventoryLabel,
        min,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        max,
        applyDefaultOrderSettings,
        unitOfMeasure,
        callbacks,
        state,
        resources,
        quantityLimitsMessages,
        telemetryContent,
        app: { config: appConfig },
        // catalogs,
        configure,
        data: { productAttributes, productUnitsOfMeasure, product }
    } = props;

    const productAttributeValues = productAttributes?.result ?? (product as any)?.AttributeValues;
    const attributesList = productAttributeValues?.slice?.() ?? productAttributeValues ?? [];
    const productClassification = getProductClassificationDisplayValue(attributesList);
    const isPvoItem = isPvoProductClassification(productClassification);
    const caseDescriptionAttributeValue = getCaseDescriptionAttributeValue(attributesList);
    const displayAttributes = getPdpDisplayAttributes(productAttributeValues, isPvoItem);
    const uomList = productUnitsOfMeasure?.result ?? [];
    const hasMultipleUOMs = uomList.length > 1;
    // const defaultUOM = props.data.product.result?.DefaultUnitOfMeasure ?? uomList[0]?.Symbol ?? '';
    const defaultUOM = React.useMemo(() => {
        const rawDefault = props.data.product.result?.DefaultUnitOfMeasure || getBaseUomSymbol(uomList) || '';
        const matched = uomList.find(u => (u.Symbol ?? '').toLocaleLowerCase() === rawDefault.toLocaleLowerCase());
        return matched?.Symbol ?? rawDefault;
    }, [props.data.product.result, uomList]);
    const [selectedUOM, setSelectedUOM] = React.useState<string>(defaultUOM);
    const [isAddingToCartWithUOM, setIsAddingToCartWithUOM] = React.useState(false);
    const [addToCartError, setAddToCartError] = React.useState<string | null>(null);

    const uomConversions = React.useMemo(() => getUomConversionMap(uomList), [uomList]);
    const baseAvailableQuantity = props?.data?.productAvailableQuantity?.result?.[0]?.ProductAvailableQuantity?.AvailableQuantity ?? 0;
    const [availableQuantity, setAvailableQuantity] = React.useState(0);
    React.useEffect(() => {
        setAvailableQuantity(
            isPvoItem
                ? convertAvailableQuantityToUOM(baseAvailableQuantity, selectedUOM, uomConversions)
                : baseAvailableQuantity
        );
    }, [baseAvailableQuantity, selectedUOM, uomConversions, isPvoItem]);

    // const availableQuantity = React.useMemo(
    //     () => (isPvoItem ? convertAvailableQuantityToUOM(baseAvailableQuantity, selectedUOM, uomConversions) : baseAvailableQuantity),
    //     [isPvoItem, baseAvailableQuantity, selectedUOM, uomConversions]);   

    React.useEffect(() => {
        setSelectedUOM(defaultUOM);
    }, [defaultUOM]);

    const handleAddToCartWithUOM = React.useCallback(async () => {
    const product = props.data.product.result;

    if (!product || !selectedUOM) {
        return;
    }

    setAddToCartError(null);
    setIsAddingToCartWithUOM(true);

    try {
        // Get current cart
        const cartState = await getCartState(props.context.actionContext);

        const requestedQty = state.quantity ?? 1;

        const existingCartQuantity =
            cartState.cart?.CartLines
                ?.filter(line => {
                    const sameItem =
                        line.ItemId?.toLocaleLowerCase() ===
                        product.ItemId?.toLocaleLowerCase();

                    const cartUOM =
                        (line.UnitOfMeasureSymbol ?? '').toLocaleLowerCase();

                    const selectedUOMNormalized =
                        selectedUOM.toLocaleLowerCase();

                    const sameUOM =
                        cartUOM === selectedUOMNormalized;

                    return sameItem && sameUOM && !line.IsVoided;
                })
                .reduce(
                    (total, line) => total + (line.Quantity ?? 0),
                    0
                ) ?? 0;

        const remainingQuantity = Math.max(
            availableQuantity - existingCartQuantity,
            0
        );

        if (isPvoItem && appConfig.enableStockCheck) {
            if (remainingQuantity <= 0) {
                setAddToCartError(
                    `You already have ${existingCartQuantity} ${selectedUOM} in your cart. No additional quantity is available.`
                );
                return;
            }

            if (requestedQty > remainingQuantity) {
                setAddToCartError(
                    `Only ${remainingQuantity} ${selectedUOM} more available. You already have ${existingCartQuantity} ${selectedUOM} in your cart.`
                );
                return;
            }
        }

        // Pass selected UOM to Commerce
        const productWithUOM = {
            ...product,
            DefaultUnitOfMeasure: selectedUOM
        };

        const catalogId = getCatalogId(props.context.request);

        const result = await cartState.addProductToCart({
            product: productWithUOM,
            count: requestedQty,
            enableStockCheck: false,
            catalogId
        });

        if (result.status === 'SUCCESS') {
            const addToCartBehavior = appConfig.addToCartBehavior;

            if (addToCartBehavior === AddToCartBehavior.showNotification) {
                NotificationsManager.instance().addNotification(
                    new SuccessNotification(
                        'Item added to cart',
                        props.resources.closeNotificationLabel ?? 'Close'
                    )
                );
            } else if (addToCartBehavior === AddToCartBehavior.goToCart) {
                const cartUrl = getUrlSync(
                    'cart',
                    props.context.actionContext
                );

                if (cartUrl) {
                    window.location.assign(cartUrl);
                }

                return;
            }
        } else {

            const specificMessage =
                result.errorDetails?.LocalizedMessage ||
                (result.errorDetails as any)?.Message ||
                (result.errorDetails as any)?.ErrorCode;

            setAddToCartError(
                specificMessage ??
                    props.resources.addedToCartFailureMessage ??
                    'Add to cart failed. Please refresh and retry.'
            );
        }
    } catch (err) {
        setAddToCartError(
            props.resources.addedToCartFailureMessage ??
                'Add to cart failed. Please refresh and retry.'
        );
    } finally {
        setIsAddingToCartWithUOM(false);
    }
}, [
    props.data.product.result,
    props.context.actionContext,
    props.context.request,
    props.resources.addedToCartFailureMessage,
    props.resources.closeNotificationLabel,
    selectedUOM,
    state.quantity,
    availableQuantity,
    isPvoItem,
    appConfig.enableStockCheck,
    appConfig.addToCartBehavior
]);

    const preferredStoreInfo: IStoreInfo | undefined = props.data.storeSelectorStateManager.result?.preferredStore;
    const preferredStoreId = preferredStoreInfo?.StoreId ? preferredStoreInfo.StoreId : '';
    const preferredStoreName = preferredStoreInfo?.StoreName ? preferredStoreInfo.StoreName : '';

    const [isStoreChange, setIsStoreChange] = React.useState<boolean>(false);

    const getInventory = React.useCallback(async () => {
        // const input = new GetFullAvailableInventoryNearbyInput(
        //     props.data.product.result?.RecordId,
        //     BuyBoxConstants.zero,
        //     BuyBoxConstants.zero,
        //     BuyBoxConstants.zero,
        //     BuyBoxConstants.zero,
        //     true
        // );

        // await getFullAvailableInventoryNearby(input, props.context.actionContext)
        //     .catch((error: Error) => {
        //         props.telemetry.error(error.message);
        //     })
        //     .then(response => {
        //         if (response && ArrayExtensions.hasElements(response)) {
        //             const isPreferredStore = (value: IFullOrgUnitAvailability) =>
        //                 value.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === preferredStoreId;
        //             const filteredFullOrgUnitAvailability = response.filter(isPreferredStore);
        //             const preferredStoreAvailability = ArrayExtensions.hasElements(filteredFullOrgUnitAvailability)
        //                 ? filteredFullOrgUnitAvailability[BuyBoxConstants.zero]
        //                 : undefined;
        //             const stockStatusLabel: string | undefined = getStockInventoryLabel(preferredStoreAvailability);
        //             const isInStock = isProductInStock(preferredStoreAvailability?.OrgUnitAvailability?.ItemAvailabilities);
        //             const resourceLabel = isInStock ? props.resources.inStockText : props.resources.outOfStockText;
        //             const stockText: string | undefined = stockStatusLabel ? stockStatusLabel : resourceLabel;
        //             const preferredStoreAvailabilityText = `${format(
        //                 props.resources.availabilityAtPreferredStoreText,
        //                 preferredStoreName
        //             )} ${stockText}`;
        //             setAvailability(preferredStoreAvailabilityText);
        //         }
        //     });

        setIsStoreChange(false);
    }, [
        props.data.product.result?.RecordId,
        appConfig.enableStockCheck,
        preferredStoreId,
        preferredStoreName,
        props.context.actionContext,
        props.resources.availabilityAtPreferredStoreText,
        props.resources.inStockText,
        props.resources.outOfStockText,
        props.telemetry
    ]);

    React.useEffect(() => {
        if (props.config.displayStockAvailability) {
            getInventory().catch((error: Error) => {
                props.telemetry.error(error.message);
            });
        }
    }, [getInventory, isStoreChange, props.telemetry, props.config.displayStockAvailability]);

    reaction(
        () => props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
        () => {
            setIsStoreChange(true);
        }
    );
    const renderTabsSection = () => {
        return (
            <div className='msc-product-tabs'>
                <div className='msc-product-tabs__header'>
                    <button className='msc-product-tabs__tab active'>Product details</button>
                </div>

                <div className='msc-product-tabs__content'>
                    <div className='msc-product-tabs__panel'>
                        {productAttributeValues ? (
                            displayAttributes.length ? (
                                <table className='msc-product-attributes-table'>
                                    <thead>
                                        <tr>
                                            <th>Attribute</th>
                                            <th>Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayAttributes.map((attr, idx) => (
                                            <tr key={attr.recordId ?? `${attr.key}-${idx}`}>
                                                <td>{attr.name}</td>
                                                <td>{attr.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p>No product details available.</p>
                            )
                        ) : (
                            <p>Loading product details...</p>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const extractText = (input: React.ReactNode): string => {
        if (typeof input === 'string' || typeof input === 'number') {
            return input.toString();
        }
        if (Array.isArray(input)) {
            return input.map(child => extractText(child)).join(' ');
        }
        if (React.isValidElement(input)) {
            // TypeScript-safe access to children
            const children = (input.props as { children?: React.ReactNode }).children;
            return extractText(children);
        }
        return ''; // null, undefined, boolean, etc.
    };

    const renderCaseDescriptionTable = (sourceText: string): JSX.Element => {
        const tableData = parseCaseDescriptionTableData(sourceText);

        return (
            <table>
                <thead>
                    <tr>
                        <th>Case #</th>
                        <th>Bacteria Name</th>
                    </tr>
                </thead>
                <tbody>
                    {tableData.map((item, index) => (
                        <tr key={index}>
                            <td>{item.caseNumber}</td>
                            <td>{item.bacteriaName}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderDescription = (descriptionInput: React.ReactNode) => {
        const descriptionText = extractText(descriptionInput);

        if (isPvoItem) {
            const hasCaseDataInAttribute = /Case\s*#/i.test(caseDescriptionAttributeValue);
            if (hasCaseDataInAttribute) {
                return renderCaseDescriptionTable(caseDescriptionAttributeValue);
            }
            return <div>{descriptionText}</div>;
        }

        const hasCaseDataInDescription = /Case\s*#/i.test(descriptionText);
        if (hasCaseDataInDescription) {
            return renderCaseDescriptionTable(descriptionText);
        }

        return <div>{descriptionText}</div>;
    };

    // const availableQuantity = props?.data?.productAvailableQuantity?.result?.[0]?.ProductAvailableQuantity?.AvailableQuantity ?? 0;
    const isOutOfStock = isPvoItem && availableQuantity <= 0;
    const outOfStockButtonText = props.resources.errorMessageOutOfStock ?? 'This item is out of stock';

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {/* {catalogs && catalogs[0].Name} */}
                <div className='ms-buybox__product-title-wrap'>
                    {title}
                    <div className='ms-buybox__unit-of-measure-wrap'>
                        <h5>Unit of Measure: </h5>
                        {hasMultipleUOMs ? (
                            <select
                                className='ms-buybox__uom-select'
                                value={selectedUOM}
                                onChange={e => setSelectedUOM(e.target.value)}
                                aria-label='Unit of measure'
                            >
                                {uomList
                                    .filter(uom => [1, 3].includes(uom.UnitOfMeasureSourceValue ?? -1))
                                    .map(uom => (
                                        <option key={uom.Symbol ?? uom.Description ?? ''} value={uom.Symbol ?? ''}>
                                            {uom.Description ?? uom.Symbol ?? ''}
                                        </option>
                                    ))}
                            </select>
                        ) : (
                            unitOfMeasure
                        )}
                    </div>
                </div>

                <Node className='msc-buybox__category'>{/* <div className='msc-buybox__category-text'>Vaccine | Avian</div> */}</Node>
                {configure && renderConfigure(configure)}
                <Node className='msc-buybox__description-section'>
                    {/* <div className='msc-buybox__description-section-text'>{props.resources.descriptionText}</div> */}
                    {renderDescription(description)}
                </Node>
                <Node className='msc-buybox__bulk-purchase-section'>
                    <div className='msc-buybox__bulk-purchase-button-text'>
                        <span>Quantity</span>
                        {quantity &&
                            renderQuantity(
                                quantity,
                                callbacks,
                                props,
                                state,
                                resources,
                                quantityLimitsMessages,
                                min,
                                max,
                                applyDefaultOrderSettings,
                                telemetryContent
                                // unitOfMeasure
                            )}
                        {props.bulkPurchaseLink}
                    </div>
                </Node>
                {isPvoItem && (
                    <div className='msc-buybox__inventory-wraper'>
                        {inventoryLabel}
                        {availableQuantity !== undefined && availableQuantity > 0 && <>({availableQuantity})</>}
                    </div>
                )}
                {hasMultipleUOMs ? (
                    <Node {...(addToCart?.ContainerProps ?? { className: 'ms-buybox__add-to-cart-container' })}>
                        {addToCart?.errorBlock}
                        {addToCartError && (
                            <div className='msc-add-to-cart__error' role='alert'>
                                {addToCartError}
                            </div>
                        )}
                        <button
                            type='button'
                            className={`msc-add-to-cart${isOutOfStock ? ' msc-add-to-cart--out-of-stock' : ''}`}
                            onClick={handleAddToCartWithUOM}
                            disabled={isOutOfStock || isAddingToCartWithUOM}
                        >
                            {isOutOfStock ? outOfStockButtonText : isAddingToCartWithUOM ? 'Adding...' : props.resources.addToCartText}
                        </button>
                    </Node>
                ) : isOutOfStock ? (
                    <Node {...(addToCart?.ContainerProps ?? { className: 'ms-buybox__add-to-cart-container' })}>
                        {addToCart?.errorBlock}
                        <button type='button' className='msc-add-to-cart msc-add-to-cart--out-of-stock' disabled>
                            {outOfStockButtonText}
                        </button>
                    </Node>
                ) : (
                    renderAddToCart(addToCart)
                )}
                {addToOrderTemplate && renderAddToOrderTemplate(props, state, callbacks)}
                {/* {ArrayExtensions.hasElements(props.slots.productSpecification) ? (
                    <Node className='msc-buybox__product-specification'>{props.slots.productSpecification[0]}</Node>
                ) : null} */}
            </Node>
            <Node className='msc-buybox__tabs-container'>{renderTabsSection()}</Node>
        </Module>
    );
};

export default BuyboxView;
